import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
  CleanTableExpandableRow,
  CleanTableExpandIcon,
  CleanTableExpandedContent,
  useExpandableTable,
} from "../ui/clean-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Plus, Shield, Edit, Trash2, User } from "lucide-react";

const ControlTable = ({
  controls,
  canManage,
  onEditControl,
  onDeleteControl,
  onCreateControl,
}) => {
  // Use the expandable table hook
  const { isExpanded, toggleRow } = useExpandableTable();

  if (controls.length === 0) {
    return (
      <Card className="border-0 shadow-none">
        <CardContent>
          <div className="text-center py-12">
            <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Controls Found</h3>
            <p className="text-muted-foreground mb-4">
              No controls have been created yet.
            </p>
            {canManage && onCreateControl && (
              <Button
                size="sm"
                onClick={onCreateControl}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Your First Control
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-none">
      <CardContent>
        <TooltipProvider>
          <CleanTable>
            <CleanTableHeader>
              <CleanTableRow>
                <CleanTableHead className="w-8"></CleanTableHead>
                <CleanTableHead>Control ID</CleanTableHead>
                <CleanTableHead>Control Name</CleanTableHead>
                <CleanTableHead>Category</CleanTableHead>
                <CleanTableHead>Sub-Category</CleanTableHead>
                <CleanTableHead className="text-center">
                  Assigned Users
                </CleanTableHead>
                {canManage && (
                  <CleanTableHead className="text-right">
                    Actions
                  </CleanTableHead>
                )}
              </CleanTableRow>
            </CleanTableHeader>
            <CleanTableBody>
              {controls.map((control) => (
                <React.Fragment key={control.id}>
                  {/* Main Control Row */}
                  <CleanTableExpandableRow
                    isExpanded={isExpanded(control.id)}
                    onToggle={(e) => {
                      // Prevent expansion when clicking action buttons
                      if (e.target.closest(".action-buttons")) {
                        e.stopPropagation();
                        return;
                      }
                      toggleRow(control.id);
                    }}
                  >
                    <CleanTableExpandIcon isExpanded={isExpanded(control.id)} />
                    <CleanTableCell>
                      <span className="text-sm font-mono text-gray-600">
                        {control.custom_id || control.id?.slice(0, 8)}
                      </span>
                    </CleanTableCell>
                    <CleanTableCell>
                      <div>
                        <p className="font-medium text-gray-900">
                          {control.name || "No name provided"}
                        </p>
                      </div>
                    </CleanTableCell>
                    <CleanTableCell>
                      {control.SubCategory?.Category ? (
                        <span className="text-gray-900">
                          {control.SubCategory.Category.name}
                        </span>
                      ) : control.categories &&
                        control.categories.length > 0 ? (
                        <div className="space-y-1">
                          {control.categories.slice(0, 2).map((category) => (
                            <div
                              key={category.id}
                              className="text-gray-900 text-sm"
                            >
                              {category.name}
                            </div>
                          ))}
                          {control.categories.length > 2 && (
                            <span className="text-xs text-muted-foreground">
                              +{control.categories.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : control.Category ? (
                        <span className="text-gray-900">
                          {control.Category.name}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">
                          Uncategorized
                        </span>
                      )}
                    </CleanTableCell>
                    <CleanTableCell>
                      {control.SubCategory ? (
                        <span className="text-gray-900">
                          {control.SubCategory.name}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">
                          No sub-category
                        </span>
                      )}
                    </CleanTableCell>
                    <CleanTableCell className="text-center">
                      <div className="flex justify-center">
                        {control.assigned_users &&
                        control.assigned_users.length > 0 ? (
                          <div className="flex -space-x-2">
                            {control.assigned_users
                              .slice(0, 3)
                              .map((user, index) => (
                                <Tooltip key={user.id || index}>
                                  <TooltipTrigger>
                                    <Avatar className="h-8 w-8 border-2 border-white hover:z-10 relative">
                                      <AvatarImage
                                        src={user.profile_pic}
                                        alt={user.name || user.email}
                                      />
                                      <AvatarFallback className="text-xs">
                                        {(user.name || user.email)
                                          .split(" ")
                                          .map((n) => n[0])
                                          .join("")
                                          .toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{user.name || user.email}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ))}
                            {control.assigned_users.length > 3 && (
                              <div className="h-8 w-8 border-2 border-white bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                                +{control.assigned_users.length - 3}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">None</span>
                        )}
                      </div>
                    </CleanTableCell>
                    {canManage && (
                      <CleanTableCell className="text-right">
                        <div className="flex items-center justify-end gap-1 action-buttons">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditControl(control);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit Control</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteControl(control);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete Control</TooltipContent>
                          </Tooltip>
                        </div>
                      </CleanTableCell>
                    )}
                  </CleanTableExpandableRow>

                  {/* Expanded Control Details Content */}
                  {isExpanded(control.id) && (
                    <CleanTableExpandedContent colSpan={canManage ? 7 : 6}>
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          Control Details
                        </h4>

                        {/* Control Information */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">
                              Key Control:
                            </span>
                            <p className="text-gray-900">
                              {control.is_key_control ? "Yes" : "No"}
                            </p>
                          </div>

                          <div>
                            <span className="font-medium text-gray-700">
                              Type:
                            </span>
                            <p className="text-gray-900">
                              {control.is_preventive
                                ? "Preventive"
                                : "Detective"}
                            </p>
                          </div>

                          <div>
                            <span className="font-medium text-gray-700">
                              Automation:
                            </span>
                            <p className="text-gray-900">
                              {control.is_automated ? "Automated" : "Manual"}
                            </p>
                          </div>

                          <div>
                            <span className="font-medium text-gray-700">
                              Frequency:
                            </span>
                            <p className="text-gray-900">
                              {control.frequency || "Not specified"}
                            </p>
                          </div>

                          <div>
                            <span className="font-medium text-gray-700">
                              Created Date:
                            </span>
                            <p className="text-gray-900">
                              {control.created_at
                                ? new Date(
                                    control.created_at
                                  ).toLocaleDateString()
                                : "N/A"}
                            </p>
                          </div>
                        </div>

                        {/* Description */}
                        {control.description && (
                          <div className="mt-3">
                            <span className="font-medium text-gray-700">
                              Description:
                            </span>
                            <p className="text-gray-900 mt-1">
                              {control.description}
                            </p>
                          </div>
                        )}

                        {/* All Categories (if multiple) */}
                        {control.categories &&
                          control.categories.length > 2 && (
                            <div>
                              <span className="font-medium text-gray-700">
                                All Categories:
                              </span>
                              <div className="mt-1 space-y-1">
                                {control.categories.map((category) => (
                                  <div
                                    key={category.id}
                                    className="text-sm text-gray-900"
                                  >
                                    {category.name}
                                    {category.sources?.some(
                                      (s) => s.type === "auto_risk_association"
                                    ) && " (Auto-assigned from risk)"}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {/* Assigned Users */}
                        {control.assigned_users &&
                          control.assigned_users.length > 0 && (
                            <div>
                              <span className="font-medium text-gray-700 flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Assigned Users ({control.assigned_users.length})
                              </span>
                              <div className="mt-2 space-y-2">
                                {control.assigned_users.map((user, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center gap-3"
                                  >
                                    <Avatar className="h-8 w-8">
                                      <AvatarImage
                                        src={user.profile_pic}
                                        alt={user.name || user.email}
                                      />
                                      <AvatarFallback className="text-xs">
                                        {(user.name || user.email)
                                          .split(" ")
                                          .map((n) => n[0])
                                          .join("")
                                          .toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {user.name || user.email}
                                      </p>
                                      {user.email && user.name && (
                                        <p className="text-xs text-gray-600">
                                          {user.email}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    </CleanTableExpandedContent>
                  )}
                </React.Fragment>
              ))}
            </CleanTableBody>
          </CleanTable>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};

export default ControlTable;
